import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { OrganizationFormData } from 'features/my-profile/become-fundraiser/form/components/add-organization/useAddOrganizationForm';
import { AccountType, BecomeFundraiserRequestDto, BecomeFundraiserResponseDto } from 'services/user/userService.dto';

import {
  MAX_BANK_ACCOUNT_NUMBER_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_PHONE_LENGTH,
  MIN_BANK_ACCOUNT_NUMBER_LENGTH,
  MIN_PHONE_LENGTH,
  ROUTING_NUMBER_LENGTH,
} from './becomeFundraiserUtils';
import { convertFormDataToBecomeFundraiserDto } from './converters';

export interface BecomeFundraiserFormData {
  isIndividual?: boolean;
  organization?: OrganizationFormData;
  routingNumber?: string;
  bankName?: string;
  accountNumber?: string;
  accountNumberConfirmation?: string;
  accountType?: AccountType;
  mobilePhone?: string;
  email?: string;
}

export const useBecomeFundraiserForm = (
  becomeFundraiser: (request: BecomeFundraiserRequestDto) => void,
  fundraiserData?: BecomeFundraiserResponseDto
) => {
  const { t } = useTranslation('myProfile');

  const onSubmit = (data: BecomeFundraiserFormData) => {
    const newBecomeFundraiserRequest: BecomeFundraiserRequestDto = convertFormDataToBecomeFundraiserDto(data);
    becomeFundraiser(newBecomeFundraiserRequest);
  };

  const validationSchema = Yup.object().shape({
    isIndividual: Yup.boolean().required(),
    organization: Yup.object().required(),
    routingNumber: Yup.string()
      .trim()
      .matches(/^\d+$/, t('form-inputs.routing-number-invalid-length'))
      .length(ROUTING_NUMBER_LENGTH, t('form-inputs.routing-number-invalid-length')),
    bankName: Yup.string()
      .trim()
      .when('routingNumber', {
        is: (value: string) => !!value,
        then: schema => schema.required(t('form-inputs.routing-number-invalid')),
      }),
    accountNumber: Yup.string()
      .trim()
      .matches(/^\d+$/, t('form-inputs.not-allowed-chars'))
      .min(MIN_BANK_ACCOUNT_NUMBER_LENGTH, t('form-inputs.bank-account-number-too-short'))
      .max(MAX_BANK_ACCOUNT_NUMBER_LENGTH),
    accountNumberConfirmation: Yup.string()
      .trim()
      .when('accountNumber', {
        is: (value: string) => !!value,
        then: schema =>
          schema
            .required(t('form-inputs.bank-account-number-no-match'))
            .oneOf([Yup.ref('accountNumber')], t('form-inputs.bank-account-number-no-match')),
      }),
    accountType: Yup.string(),
    mobilePhone: Yup.string()
      .trim()
      .matches(/^\+\d*$/, t('form-inputs.phone-invalid'))
      .min(MIN_PHONE_LENGTH, t('form-inputs.phone-too-short'))
      .max(MAX_PHONE_LENGTH)
      .required(t('form-inputs.phone-required')),
    email: Yup.string()
      .trim()
      .email(t('form-inputs.email-invalid'))
      .max(MAX_EMAIL_LENGTH)
      .required(t('form-inputs.email-required')),
  });

  const initialValues = {
    isIndividual: fundraiserData?.fundraiserType === 'PRIVATE',
    organization: fundraiserData
      ? {
          isNonProfit: fundraiserData.fundraiserType !== 'FOR_PROFIT',
          name: fundraiserData.organizationName!,
          ein: fundraiserData.ein!,
          state: fundraiserData.state,
          zipCode: fundraiserData.zipCode,
          city: fundraiserData.city,
          country: fundraiserData.country,
          addressLine1: fundraiserData.addressLine1,
          addressLine2: fundraiserData.addressLine2,
        }
      : undefined,
    mobilePhone: fundraiserData?.mobilePhone,
    email: fundraiserData?.email,
  };

  return useFormik({ initialValues, validationSchema, onSubmit });
};
